import React, { useState } from "react";
import styles from "./BrandOverview.module.scss";
import Button from "components/core/Button/Button";
import classNames from "classnames";
import Container from "components/core/Layout/Container/Container";
import {
  BrandDetailDocument,
  CompanySubBrand,
} from "@tiicker/util/lib/contentful/types";
import { IexCompany } from "$gql/types.gen";
import {
  getImageReference,
  getItemReference,
} from "@tiicker/util/lib/contentful/helpers";
import Markdown from "components/Markdown/Markdown";
import SwiperCarousel from "components/Carousel/SwiperCarousel";
import VideoLoader from "components/VideoLoader/VideoLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRight } from "@fortawesome/sharp-light-svg-icons";

type FeatureBarProps = {
  brand?: BrandDetailDocument;
  company?: IexCompany;
  layout: "column" | "row";
};

const FeatureBar = ({ brand, company, layout }: FeatureBarProps) => {
  const barClassNames = {
    [styles.BrandOverview__barLayoutColumn]: layout === "column",
    [styles.BrandOverview__barLayoutRow]: layout === "row",
  };

  const wrappedClassNames = classNames(
    styles.BrandOverview__bar,
    barClassNames
  );

  const ceo = brand?.ceoName || company?.CEO;

  const headQuarters = brand?.headquarters
    ? brand.headquarters
    : company?.city && company?.state
    ? `${company.city}, ${company.state}`
    : null;

  const foundedYear = brand?.foundedYear;

  const employeeCount = brand?.employeeCount || company?.employees;

  if (!ceo && !headQuarters && !foundedYear && !employeeCount) {
    return <></>;
  }

  return (
    <div className={wrappedClassNames}>
      {ceo && (
        <div className={styles.BrandOverview__barFeature}>
          <h6>CEO</h6>
          <p>{ceo}</p>
        </div>
      )}
      {headQuarters && (
        <div className={styles.BrandOverview__barFeature}>
          <h6>Headquarters</h6>
          <p>{headQuarters}</p>
        </div>
      )}
      {foundedYear && (
        <div className={styles.BrandOverview__barFeature}>
          <h6>Founded</h6>
          <p>{foundedYear}</p>
        </div>
      )}
      {employeeCount &&
        (!brand ||
          (brand.tickerSymbol.toLowerCase() !== "lgf.a" &&
            brand.tickerSymbol.toLowerCase() !== "lgf.b")) && (
          <div className={styles.BrandOverview__barFeature}>
            <h6>Employees</h6>
            <p>{employeeCount}</p>
          </div>
        )}
    </div>
  );
};

type Props = {
  brand?: BrandDetailDocument;
  company?: IexCompany;
  includes?: any;
};

const BrandOverview = (props: Props) => {
  const { brand, company } = props;

  const contentContainerClassNames = {
    [styles.BrandOverview__contentColumnReverse]: brand !== undefined,
  };

  const wrappedContentContainerClassNames = classNames(
    styles.BrandOverview__content,
    contentContainerClassNames
  );

  const companyDescription = brand
    ? brand.description || ""
    : (company?.description as string | undefined) || "";

  const limitedDescription = companyDescription
    ?.split(" ")
    ?.slice(0, 68)
    .join(" ");

  const mediaImages = brand?.secondaryImageGallery?.fields?.images;
  const videos = brand?.videos ? brand.videos.map((x) => x.sys.id) : [];

  const imageSlides =
    mediaImages && mediaImages.length > 0
      ? mediaImages.map((image) => {
          return (
            <img
              className={styles.BrandOverview__heroImage}
              src={image.fields.file.url}
            />
          );
        })
      : [];
  const videosSlides =
    videos && videos.length > 0
      ? videos.map((video) => (
          <VideoLoader
            videoIds={[video]}
            thumbnailClassName={styles.BrandOverview__heroImage}
            noRow
            stopAutoplay
          />
        ))
      : [];
  const slides = brand?.showVideosFirst
    ? videosSlides.concat(imageSlides)
    : imageSlides.concat(videosSlides);

  const [descriptionIsOpen, setDescriptionIsOpen] = useState<boolean>(
    limitedDescription?.length === companyDescription?.length
  );

  const partnerInformation = brand?.brandDetailOverflow?.fields || null;

  return (
    <div className={styles.BrandOverview}>
      <Container>
        <div className={styles.BrandOverview__layoutContainer}>
          <div className={wrappedContentContainerClassNames}>
            {slides.length > 0 ? (
              <div className={styles.carouselContainer}>
                <SwiperCarousel
                  slides={slides}
                  slidesPerView={1}
                  slidesPerGroup={1}
                  spaceBetween={0}
                  pagination
                  navigation
                  openSide="both"
                />
              </div>
            ) : (
              <FeatureBar company={company} brand={brand} layout="column" />
            )}
            <div className={styles.BrandOverview__headerContainer}>
              {companyDescription.length > 0 && (
                <>
                  <h2>{`About ${brand ? brand.brandName : ""}`}</h2>
                  <Markdown
                    className={classNames(styles.BrandOverview__aboutText, {
                      [styles.BrandOverview__aboutText__Open]:
                        descriptionIsOpen,
                    })}
                    markdownText={companyDescription}
                  />
                  {!descriptionIsOpen && (
                    <a
                      className={styles.readMoreLink}
                      onClick={() => setDescriptionIsOpen(true)}
                    >
                      Read More
                    </a>
                  )}
                </>
              )}
              <div>
                {brand && brand.companyWebsite && (
                  <Button
                    href={brand.companyWebsite}
                    className={styles.BrandOverview__cta}
                    type={partnerInformation ? "primary" : "secondary"}
                    target="_blank"
                  >
                    Visit {brand.brandName}
                  </Button>
                )}
                {partnerInformation?.partnerUrl && (
                  <Button
                    href={partnerInformation.partnerUrl}
                    className={styles.BrandOverview__cta__secondary}
                    type="secondary"
                    target="_blank"
                  >
                    {partnerInformation.partnerTitle}{" "}
                    <FontAwesomeIcon icon={faArrowUpRight} />
                  </Button>
                )}
              </div>
            </div>
          </div>
          {mediaImages && (
            <FeatureBar company={company} brand={brand} layout="row" />
          )}

          {brand && brand.subBrands && (
            <>
              <div className={styles.BrandOverview__ourBrandsHeader}>
                <h3>Brands</h3>
              </div>
              <div className={styles.BrandOverview__logos}>
                {brand.subBrands.map((b, idx) => {
                  const subBrand = getItemReference<CompanySubBrand>(b, null);

                  if (!subBrand) {
                    return <></>;
                  }

                  const logo = getImageReference(subBrand.fields.brandLogo);

                  if (!logo) {
                    return <></>;
                  }

                  return (
                    <a
                      target="_blank"
                      className={styles.BrandOverview__logoLink}
                      href={subBrand.fields.website}
                    >
                      <img key={idx} src={logo.fields.file.url} />
                    </a>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default BrandOverview;
